import { Table } from '@components/table/Table';
import { TableActions } from '@components/table/tableActions';
import { type CellContext } from '@tanstack/react-table';
import useDisclosure from 'hooks/useDisclosure';
import { getAssetTypeReadableNameFromStringEnum } from 'utils/assetUtils/assetUtils';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGetTagsTableColumn from '../hooks/useGetTagsTableColumn';
import {
  useDeleteTag,
  useListCustomProperties,
} from 'api/customProperty/customPropertyService';
import { TagDetailsModal } from '../modal/TagDetailsModal';
import EditTagModal from '../modal/EditTagModal/EditTagModal';
import DeleteTagModal from '../modal/DeleteTagModal';

interface TagsTableProps {
  query?: string;
  filters?: {
    type?: string;
    assetFunction?: string;
  };
}
const TagsTable: FC<TagsTableProps> = ({ query, filters }) => {
  const [propertyDefinitionId, setpropertyDefinitionId] = useState<number>();
  const { t } = useTranslation();

  const {
    isOpen: isDetailsOpen,
    handleClose: handleDetailsClose,
    handleOpen: handleDetailsOpen,
  } = useDisclosure();

  const {
    isOpen: isEditPropertyDefinitionOpen,
    handleClose: handleEditPropertyDefinitionClose,
    handleOpen: handleEditPropertyDefinitionOpen,
  } = useDisclosure();

  const {
    isOpen: isDeletePropertyDefinitionOpen,
    handleClose: handleDeletePropertyDefinitionClose,
    handleOpen: handleDeletePropertyDefinitionOpen,
  } = useDisclosure();

  const { data, isFetching, fetchNextPage, isFetchingNextPage } =
    useListCustomProperties({
      query,
    });

  const { mutate: deleteTagMutate, isPending: isLoadingDeleteTag } =
    useDeleteTag();

  const totalDBRowCount = data?.pages?.[0]?.total ?? 0;

  const flatDataWithLocation = useMemo(() => {
    if (!data?.pages) return [];
    const flattenCustomProperties = data.pages.flatMap(
      (page) => page?.property_definitions || []
    );

    const modifiedCustomProperties = flattenCustomProperties.map(
      (customProperty) => {
        return {
          ...customProperty,
          type: getAssetTypeReadableNameFromStringEnum(
            customProperty.data_type
          ),
        };
      }
    );

    return modifiedCustomProperties;
  }, [data]);

  const createTableActions = (info: CellContext<any, any>): ReactNode => {
    const id: number = info?.row?.original?.id;
    const actions = [
      {
        name: t('common.actions.edit'),
        onClick: () => {
          setpropertyDefinitionId(id);
          handleEditPropertyDefinitionOpen();
        },
      },
      {
        name: t('common.actions.delete'),
        onClick: () => {
          setpropertyDefinitionId(id);
          handleDeletePropertyDefinitionOpen();
        },
      },
    ];
    return <TableActions key={`action-${id}`} actions={actions} />;
  };

  const handleDeletePropertyDefinition = (): void => {
    if (propertyDefinitionId) {
      deleteTagMutate(propertyDefinitionId);
      handleDeletePropertyDefinitionClose();
    }
  };

  const handleRowClick = (rowData: any): void => {
    setpropertyDefinitionId(rowData.id);
    handleDetailsOpen();
  };

  const { columns } = useGetTagsTableColumn({ createTableActions });

  return (
    <>
      <Table
        columns={columns}
        data={flatDataWithLocation}
        enableRowSelection={true}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        totalDBRowCount={totalDBRowCount}
        rowEstimateSize={75}
        onClickRow={handleRowClick}
      />
      {propertyDefinitionId && (
        <TagDetailsModal
          tagId={propertyDefinitionId}
          isOpen={isDetailsOpen}
          handleClose={handleDetailsClose}
          handleOpen={handleDetailsOpen}
          handleEditOpen={handleEditPropertyDefinitionOpen}
        />
      )}
      {propertyDefinitionId && (
        <EditTagModal
          isOpen={isEditPropertyDefinitionOpen}
          handleClose={handleEditPropertyDefinitionClose}
          handleOpen={handleEditPropertyDefinitionOpen}
          selectedTagId={propertyDefinitionId}
        />
      )}
      <DeleteTagModal
        tagId={propertyDefinitionId}
        isOpen={isDeletePropertyDefinitionOpen}
        handleOpen={handleDeletePropertyDefinitionOpen}
        handleClose={handleDeletePropertyDefinitionClose}
        onConfirm={handleDeletePropertyDefinition}
        isLoading={isLoadingDeleteTag}
      />
    </>
  );
};

export default TagsTable;
